import { useMutation, useQueryClient, type MutationMeta } from '@tanstack/react-query';
import { IssueStatus } from '../../../trusstor/index.js';
import { useClientApis } from '../../hooks/ClientApisProvider.js';
import { beginIssuePatch, patchCachedIssue, rollbackIssue } from './issueCache.js';

/**
 * Quick-action status mutation used by the card "Complete" button. Patches the
 * issue's status optimistically; on failure rolls back.
 */
export function useQuickStatusUpdate(siteId: string | null, meta?: MutationMeta) {
  const { updateIssueApi } = useClientApis().issues;
  const qc = useQueryClient();

  const mutation = useMutation({
    meta,
    mutationFn: ({ issueId, status }: { issueId: string; status: IssueStatus }) => {
      if (!siteId) throw new Error('quickStatusUpdate called without siteId');
      return updateIssueApi(siteId, issueId, { status });
    },
    mutationKey: ['issue-status'],
    onMutate: async ({ issueId, status }) => ({
      previous: await beginIssuePatch(qc, siteId, issueId, (i) => ({ ...i, status })),
    }),
    onError: (_err, { issueId }, ctx) => rollbackIssue(qc, siteId, ctx?.previous, issueId),
    // Reconcile ONLY the status fields this mutation owns — writing the full
    // object would clobber a concurrent useUpdateIssue optimistic edit (e.g. a
    // critical toggle) that hasn't settled yet, and flicker.
    onSuccess: (updated, { issueId }) =>
      patchCachedIssue(qc, siteId, issueId, (i) => ({
        ...i,
        status: updated.status,
        ...(updated.statusUpdateDate !== undefined && { statusUpdateDate: updated.statusUpdateDate }),
        ...(updated.statusUpdateUser !== undefined && { statusUpdateUser: updated.statusUpdateUser }),
      })),
  });

  return mutation;
}
